/* eslint-disable react/style-prop-object */
import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import {ArrowDropDown, Close, Delete, GetApp} from '@material-ui/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import {Pagination} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../../../store';
import {
  deleteDeviceGroup,
  downloadDeviceGroups,
  fetchDeviceGroups,
  updateDeviceGroup,
  uploadDeviceGroup,
} from '../../../../store/prodDeployments/dashboard';
import {toast} from '../../../../base-components/StudioToast';
import {ConfirmDialog} from '../../../../base-components/StudioConfirmDialog';
import {StudioCheckbox} from '../../../../base-components/StudioCheckbox';
import {isNumber} from 'lodash';
import PlusIcon from '../../../../assets/icons/PlusIcon';
import UploadIcon from '../../../../assets/icons/UploadIcon';
import {DropArea} from '../../../../base-components/StudioDropArea';
import {FileExplorer} from '../../../TestDeployView/FileExplorer';
import {
  AddOrUpdateDeviceGroup,
  DeviceGroupInfo,
  DeviceGroupSortableColumn,
  DeviceGroupsRequest,
} from '../../../../types/deployment/DeploymentDeviceGroup';
import Waiting from '../../../../base-components/StudioWaiting/Waiting';
import DeviceGroupTableRow from './DeviceGroupTableRow';
import AddNewGroupDevice from './AddNewGroupDevice';
import {unwrapResult} from '@reduxjs/toolkit';
import {StudioTooltip} from '../../../../base-components/StudioTooltip';
import {REQUEST_POLLING_INTERVAL} from '../../../../config/constants';

const PAGE_SIZE = 6;

export type DeviceGroupInfoRow = DeviceGroupInfo & {selected: boolean};

type DeviceGroupTableProps = {
  search: string;
};

export type DeleteConfirmationData = {
  isOpen: boolean;
  deviceGroup: DeviceGroupInfo | null;
  isMultiple: boolean;
  selectedIds: string[];
};

export const DeviceGroupTable = ({search}: DeviceGroupTableProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [sort, setSort] = useState<DeviceGroupSortableColumn>('name');
  const [sortDir, setSortDir] = useState<-1 | 1>(1);
  const [page, setPage] = useState(0);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationData>({
    isOpen: false,
    deviceGroup: null,
    isMultiple: false,
    selectedIds: [],
  });
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deviceGroups, setDeviceGroups] = useState<DeviceGroupInfoRow[]>([]);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 0,
    totalResults: 0,
    noOfDisplayed: 0,
  });

  const [newGroup, setNewGroup] = useState(false);

  const handleFileListChange = (newFiles: File[]) => setFiles([...files, ...newFiles]);

  const handleFileListDelete = (fileIndex: number) =>
    setFiles(files.filter((file, index) => index !== fileIndex));

  useEffect(() => {
    setPage(0);
  }, [sort, sortDir, search]);

  useEffect(() => {
    if (isEditing) return;
    const requestBody: DeviceGroupsRequest = {
      firstPage: page,
      pageSize: PAGE_SIZE,
      paginated: true,
      orderDefinitions: [{field: sort, direction: sortDir === -1 ? 'DESC' : 'ASC'}],
      params: {
        name: search,
        autoCreated: false,
      },
    };

    let promise = dispatch(fetchDeviceGroups(requestBody));
    const interval = setInterval(() => {
      promise = dispatch(fetchDeviceGroups(requestBody));
    }, REQUEST_POLLING_INTERVAL);

    return () => {
      promise.abort();
      clearInterval(interval);
    };
  }, [search, sort, sortDir, page, dispatch, isEditing]);

  const state = useSelector((state: RootState) => state.deploymentDashboard);

  const numberOfSelectedRows = deviceGroups.filter(e => e.selected).map(e => e.id).length;

  useEffect(() => {
    const selectedRows = deviceGroups.filter(e => e.selected).map(e => e.id);
    // add selected to deviceGroups to use checkbox (for delete and download)
    const newDeviceGroups = state.deviceGroups.data.filter(d => !d.autoCreated);
    const updatedDeviceGroups = newDeviceGroups.map(deviceGroup => {
      return {
        ...deviceGroup,
        selected: selectedRows.includes(deviceGroup.id), // using this so that the selected items won't refresh
      };
    });
    setDeviceGroups(updatedDeviceGroups);
    const totalResults = state.deviceGroups.count;

    const paginationCount =
      Math.ceil(totalResults / PAGE_SIZE) === 0 ? 1 : Math.ceil(totalResults / PAGE_SIZE);
    const paginationPage = page + 1;

    const noOfDisplayed = totalResults >= PAGE_SIZE ? PAGE_SIZE : totalResults;

    setPagination({
      count: paginationCount,
      page: paginationPage,
      totalResults,
      noOfDisplayed,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, page]);

  const handleSort = (column: DeviceGroupSortableColumn) => {
    if (column === sort) {
      setSortDir(sortDir === 1 ? -1 : 1);
    } else {
      setSort(column);
      setSortDir(1);
    }
  };

  const getSortDirection = (): 'asc' | 'desc' => {
    return sortDir === 1 ? 'asc' : 'desc';
  };

  // used for toggling device groups
  const toggleCheckAndUncheckAll = () => {
    const newDeviceGroups = deviceGroups.map(e => {
      return {
        ...e,
        selected: numberOfSelectedRows === pagination.noOfDisplayed ? false : true,
      };
    });
    setDeviceGroups(newDeviceGroups);
  };

  const toggleAddNewDeviceGroup = () => {
    setNewGroup(!newGroup);
  };

  const submitUploadProject = async () => {
    setIsUploading(true);
    const action = await dispatch(uploadDeviceGroup(files));
    const res = unwrapResult(action);
    setIsUploading(false);
    toggleUploadModal();
    if (res !== undefined) {
      toast.success(intl.formatMessage({id: 'prodDeployment.deviceGroupCreated'}));
    }
  };

  const toggleUploadModal = () => {
    setFiles([]);
    setIsOpen(!isOpen);
  };

  const handleDownloadDeviceGroup = (ids: string[]) => {
    dispatch(downloadDeviceGroups(ids));
  };

  const handleUpdateDeviceGroup = async (data: AddOrUpdateDeviceGroup) => {
    await dispatch(updateDeviceGroup(data));
    toast.success(
      intl.formatMessage({id: 'prodDeployment.deviceGroupUpdated'}, {name: data.name})
    );
    setIsEditing(false);
  };

  const onOkDeleteConfirmation = async () => {
    setIsDeleting(true);
    if (deleteConfirmation.isMultiple && deleteConfirmation.selectedIds.length) {
      await dispatch(deleteDeviceGroup(deleteConfirmation.selectedIds));
      toast.success(
        intl.formatMessage(
          {id: 'prodDeployment.deviceGroupsDeleted'},
          {count: deleteConfirmation.selectedIds.length}
        )
      );
    } else if (!deleteConfirmation.isMultiple && deleteConfirmation.deviceGroup) {
      await dispatch(deleteDeviceGroup([deleteConfirmation.deviceGroup.id]));
      toast.success(
        intl.formatMessage(
          {id: 'prodDeployment.deviceGroupDeleted'},
          {name: deleteConfirmation.deviceGroup.name}
        )
      );
    }
    setDeleteConfirmation({
      isOpen: false,
      deviceGroup: null,
      isMultiple: false,
      selectedIds: [],
    });
    setIsDeleting(false);
  };

  return (
    <>
      <div className="prod-deploy-dashboard__menu__device_group">
        <Button
          onClick={toggleUploadModal}
          size="large"
          variant="outlined"
          className="prod-deploy-dashboard__actions__button"
          color="primary"
          startIcon={<UploadIcon />}
        >
          <FormattedMessage id="prodDeployment.uploadGroup" />
        </Button>
        <Button
          onClick={toggleAddNewDeviceGroup}
          size="large"
          variant="outlined"
          color="primary"
          className="prod-deploy-dashboard__actions__button"
          startIcon={<PlusIcon />}
          disabled={newGroup}
        >
          <FormattedMessage id="prodDeployment.addNewGroup" />
        </Button>
      </div>
      {numberOfSelectedRows > 0 && (
        <div className="prod-deploy-dashboard__group__select">
          <p>{`${numberOfSelectedRows} item${
            numberOfSelectedRows > 1 ? 's' : ''
          } selected`}</p>
          <div>
            <StudioTooltip
              title={intl.formatMessage({
                id: 'form.download',
              })}
              placement="top"
            >
              <GetApp
                className="prod-deploy-dashboard__group__select__download"
                onClick={() => {
                  const selectedIds = deviceGroups.filter(e => e.selected).map(e => e.id);
                  handleDownloadDeviceGroup(selectedIds);
                }}
              />
            </StudioTooltip>
            <StudioTooltip
              title={intl.formatMessage({
                id: 'form.delete',
              })}
              placement="top"
            >
              <Delete
                className="prod-deploy-dashboard__group__select__delete"
                onClick={() => {
                  const selectedIds = deviceGroups.filter(e => e.selected).map(e => e.id);
                  setDeleteConfirmation({
                    isOpen: true,
                    isMultiple: true,
                    deviceGroup: null,
                    selectedIds,
                  });
                }}
              />
            </StudioTooltip>
          </div>
        </div>
      )}
      <TableContainer
        component={Paper}
        className="prod-deploy-dashboard__deployments"
        elevation={0}
      >
        <Table stickyHeader className="prod-deploy-dashboard__table">
          <TableHead>
            <TableRow>
              <TableCell>
                <StudioCheckbox
                  checked={numberOfSelectedRows === deviceGroups.length}
                  name="select"
                  onChange={toggleCheckAndUncheckAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'name'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('name')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.deviceGroup" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.deviceGroupStatus" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.device" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.deploymentStatus" />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'deploymentName'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('deploymentName')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.deployment" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort === 'applicationName'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('applicationName')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.application" />
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newGroup && (
              <AddNewGroupDevice toggleAddNewDeviceGroup={toggleAddNewDeviceGroup} />
            )}
            {deviceGroups.length ? (
              <>
                {deviceGroups.map(row => (
                  <DeviceGroupTableRow
                    key={row.id}
                    row={row}
                    deviceGroups={deviceGroups}
                    setDeviceGroups={setDeviceGroups}
                    setDeleteConfirmation={setDeleteConfirmation}
                    handleDownloadDeviceGroup={handleDownloadDeviceGroup}
                    handleUpdateDeviceGroup={handleUpdateDeviceGroup}
                    setIsEditing={setIsEditing}
                  />
                ))}
                <TableRow>
                  <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                    <FormattedMessage
                      id="prodDeployment.deviceGroupResult"
                      values={{noOfResults: deviceGroups.length}}
                    />
                  </TableCell>
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                  <img src="images/emptyStateNoResults.png" alt="No deployment" />
                  <p>
                    <FormattedMessage id="prodDeployment.deviceGroupNoResult" />
                  </p>
                  <p>
                    <FormattedMessage id="prodDeployment.deviceGroupNoResultDesc" />
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isNumber(pagination.totalResults) && (
        <Pagination
          className="prod-deploy-dashboard__pagination"
          count={pagination.count}
          page={pagination.page}
          shape="rounded"
          onChange={(_, page) => setPage(page - 1)}
          data-testid="prod-deploy-dashboard-pagination"
          id="prod-deploy-dashboard-pagination"
        />
      )}
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'prodDeployment.deleteDeviceGroupConfirmTitle'})}
        message={intl.formatMessage({
          id: 'prodDeployment.deleteDeviceGroupConfirmMessage',
        })}
        submitLabel={intl.formatMessage({id: 'prodDeployment.deleteConfirmSubmitLabel'})}
        open={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({...deleteConfirmation, isOpen: false})}
        onOk={onOkDeleteConfirmation}
        disableSubmit={isDeleting}
      />

      <Dialog
        open={isOpen}
        onClose={toggleUploadModal}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullWidth
        className="project-form-container"
        data-testid="create-project-form"
        id="create-project-form"
      >
        <DialogTitle disableTypography={true} className="project-form-container__header">
          <h4 className="project-form-container__header__title">
            <FormattedMessage tagName="span" id="prodDeployment.uploadGroup" />
          </h4>
          <span
            className="project-form-container__header__close"
            onClick={toggleUploadModal}
          >
            <Close />
          </span>
        </DialogTitle>
        {!isUploading ? (
          <>
            <DialogContent className="content">
              <DropArea
                size="xlg"
                onChange={handleFileListChange}
                className="import-dialog__drop"
                accept={['csv', '.csv', 'application/vnd.ms-excel', 'text/plain']}
              />
              {files.length > 0 ? (
                <FileExplorer
                  files={files}
                  onDelete={(file, index) => handleFileListDelete(index)}
                  onClear={() => setFiles([])}
                  size="small"
                  icon="delete"
                  className="import-dialog__files"
                />
              ) : (
                <div className="prod-deploy-dashboard__supported__format__container">
                  <h4>
                    <FormattedMessage id="prodDeployment.deviceGroupSupportedFormat" />
                  </h4>
                  <div className="prod-deploy-dashboard__supported__format__inner">
                    <h4>
                      <FormattedMessage id="prodDeployment.deviceGroupSupportedFormatTitle" />
                    </h4>
                    <div className="prod-deploy-dashboard__supported__format__inner__container">
                      <div className="prod-deploy-dashboard__supported__format__inner__container__div">
                        <h5>
                          <FormattedMessage id="prodDeployment.deviceGroupSupportedFormatNewGroup" />
                        </h5>
                        <h6>
                          <FormattedMessage id="prodDeployment.deviceGroupSupportedFormatNewGroupDesc" />
                        </h6>
                      </div>
                      <h6>
                        <FormattedMessage id="prodDeployment.deviceGroupSupportedFormatOr" />
                      </h6>
                      <div className="prod-deploy-dashboard__supported__format__inner__container__div">
                        <h5>
                          <FormattedMessage id="prodDeployment.deviceGroupSupportedFormatExistingGroup" />
                        </h5>
                        <h6>
                          <FormattedMessage id="prodDeployment.deviceGroupSupportedFormatExistingGroupDesc" />
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Box className="prod-deploy-dashboard__upload project-form-container__footer">
                <Box className="project-form-container__footer__actions">
                  <Button onClick={toggleUploadModal} size="large" variant="outlined">
                    <FormattedMessage id="form.cancel" />
                  </Button>
                  <Button
                    onClick={submitUploadProject}
                    color="primary"
                    size="large"
                    variant="contained"
                    type="submit"
                    disabled={files.length <= 0}
                    data-testid="create-project-page-submit"
                    id="create-project-page-submit"
                  >
                    <FormattedMessage id="form.upload" />
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </>
        ) : (
          <DialogContent className="content">
            <Waiting
              diameter={120}
              loadingText={
                <>
                  <h3 className="prod-deploy-dashboard__waiting_title">
                    {intl.formatMessage({id: 'prodDeployment.uploadingDeviceGroup'})}
                  </h3>
                  <p className="prod-deploy-dashboard__waiting_desc">
                    {intl.formatMessage({
                      id: 'prodDeployment.uploadingDeviceGroupDesc',
                    })}
                  </p>
                </>
              }
              className="project-form-container__loading"
            />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
